import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import {
  FileText, Plus, Search, Calendar, DollarSign, User, Trash2,
  Edit2, X, Send, Check, Clock, AlertCircle, Loader2, 
  ChevronDown, Filter, Eye, Copy, CheckCircle, XCircle, FileWarning
} from "lucide-react";

// Status configuration
const STATUSES = {
  draft: { label: "Draft", color: "bg-slate-100 text-slate-700", icon: FileText },
  sent: { label: "Sent", color: "bg-blue-100 text-blue-700", icon: Send },
  accepted: { label: "Accepted", color: "bg-emerald-100 text-emerald-700", icon: CheckCircle },
  declined: { label: "Declined", color: "bg-red-100 text-red-700", icon: XCircle },
  expired: { label: "Expired", color: "bg-amber-100 text-amber-700", icon: FileWarning }
};

// Format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
};

// Format date
const formatDate = (dateString) => {
  if (!dateString) return null;
  return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// Status Badge Component
const StatusBadge = ({ status }) => {
  const config = STATUSES[status] || STATUSES.draft;
  const Icon = config.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.color}`}>
      <Icon className="w-3 h-3" />
      {config.label}
    </span>
  );
};

// Estimate Card Component
const EstimateCard = ({ estimate, onView, onEdit, onDelete }) => {
  return (
    <div 
      className="p-4 bg-white rounded-lg border border-slate-200 hover:shadow-md transition-all cursor-pointer"
      onClick={() => onView(estimate)}
      data-testid={`estimate-${estimate.id}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-bold text-[#1e3a5f]">{estimate.estimate_number}</span>
            <StatusBadge status={estimate.status} />
          </div>
          
          <p className="font-medium text-slate-700 truncate">{estimate.customer_name}</p>
          
          <div className="flex items-center gap-3 mt-2 text-sm text-slate-500">
            <span className="flex items-center gap-1">
              <Calendar className="w-3.5 h-3.5" />
              {formatDate(estimate.created_at)}
            </span>
            {estimate.valid_until && (
              <span className="flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" />
                Valid until {formatDate(estimate.valid_until)}
              </span>
            )}
          </div>
        </div>
        
        <div className="text-right flex-shrink-0">
          <p className="text-xl font-bold text-[#1e3a5f]">{formatCurrency(estimate.total)}</p>
          <p className="text-xs text-slate-400">{estimate.line_items?.length || 0} item(s)</p>
        </div>
      </div>
      
      {/* Quick Actions */}
      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-100" onClick={e => e.stopPropagation()}>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onView(estimate)}
          className="text-slate-600 hover:text-[#1e3a5f]"
        >
          <Eye className="w-4 h-4 mr-1" />
          View
        </Button>
        {estimate.status === "draft" && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onEdit(estimate)}
            className="text-slate-600 hover:text-[#1e3a5f]"
          >
            <Edit2 className="w-4 h-4 mr-1" />
            Edit
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onDelete(estimate)}
          className="text-slate-600 hover:text-red-500 ml-auto"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

// Line Item Row Component
const LineItemRow = ({ item, index, onChange, onRemove, canRemove }) => {
  const lineTotal = (item.quantity || 0) * (item.unit_price || 0);
  
  return (
    <div className="flex items-start gap-2 p-3 bg-slate-50 rounded-lg">
      <div className="flex-1 min-w-0">
        <Input
          placeholder="Item description"
          value={item.description || ""}
          onChange={(e) => onChange(index, "description", e.target.value)}
          className="border-slate-200 mb-2"
          data-testid={`line-item-desc-${index}`}
        />
        <div className="flex gap-2">
          <div className="w-20">
            <label className="text-xs text-slate-500">Qty</label>
            <Input
              type="number"
              min="1"
              step="1"
              value={item.quantity || 1}
              onChange={(e) => onChange(index, "quantity", parseFloat(e.target.value) || 1)}
              className="border-slate-200"
              data-testid={`line-item-qty-${index}`}
            />
          </div>
          <div className="flex-1">
            <label className="text-xs text-slate-500">Unit Price</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={item.unit_price || ""}
                onChange={(e) => onChange(index, "unit_price", parseFloat(e.target.value) || 0)}
                className="border-slate-200 pl-7"
                data-testid={`line-item-price-${index}`}
              />
            </div>
          </div>
          <div className="w-24 text-right">
            <label className="text-xs text-slate-500">Total</label>
            <p className="py-2 font-medium text-[#1e3a5f]">{formatCurrency(lineTotal)}</p>
          </div>
        </div>
      </div>
      {canRemove && (
        <button
          type="button"
          onClick={() => onRemove(index)}
          className="p-2 text-slate-400 hover:text-red-500 mt-1"
          data-testid={`remove-line-item-${index}`}
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

// Estimate Form Modal
const EstimateFormModal = ({ estimate, clients, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    customer_name: estimate?.customer_name || "",
    customer_email: estimate?.customer_email || "",
    service_description: estimate?.service_description || "",
    line_items: estimate?.line_items?.length > 0 
      ? estimate.line_items 
      : [{ description: "", quantity: 1, unit_price: 0 }],
    tax_rate: estimate?.tax_rate || 0,
    notes: estimate?.notes || "",
    valid_until: estimate?.valid_until || "",
    status: estimate?.status || "draft"
  });
  const [saving, setSaving] = useState(false);

  // Calculate totals
  const subtotal = formData.line_items.reduce((sum, item) => 
    sum + ((item.quantity || 0) * (item.unit_price || 0)), 0
  );
  const taxAmount = subtotal * (formData.tax_rate / 100);
  const total = subtotal + taxAmount;

  const handleLineItemChange = (index, field, value) => {
    const newItems = [...formData.line_items];
    newItems[index] = { ...newItems[index], [field]: value };
    setFormData({ ...formData, line_items: newItems });
  };

  const addLineItem = () => {
    setFormData({
      ...formData,
      line_items: [...formData.line_items, { description: "", quantity: 1, unit_price: 0 }]
    });
  };

  const removeLineItem = (index) => {
    const newItems = formData.line_items.filter((_, i) => i !== index);
    setFormData({ ...formData, line_items: newItems });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_name.trim()) {
      toast.error("Customer name is required");
      return;
    }
    
    if (formData.line_items.length === 0) {
      toast.error("At least one line item is required");
      return;
    }
    
    const hasValidItem = formData.line_items.some(item => 
      item.description?.trim() && item.unit_price > 0
    );
    
    if (!hasValidItem) {
      toast.error("Add at least one item with description and price");
      return;
    }
    
    setSaving(true);
    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      // Error handled in parent
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="estimate-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white z-10">
          <h2 className="text-lg font-bold text-[#1e3a5f]">
            {estimate ? `Edit ${estimate.estimate_number}` : "New Estimate"}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Customer Info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Customer Name <span className="text-red-500">*</span>
              </label>
              <Input
                value={formData.customer_name}
                onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                placeholder="Customer name"
                className="border-slate-200"
                list="clients-list"
                data-testid="estimate-customer-name"
              />
              <datalist id="clients-list">
                {clients.map(c => (
                  <option key={c.id} value={c.full_name} />
                ))}
              </datalist>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Customer Email</label>
              <Input
                type="email"
                value={formData.customer_email}
                onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                placeholder="customer@email.com"
                className="border-slate-200"
                data-testid="estimate-customer-email"
              />
            </div>
          </div>
          
          {/* Service Description */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Service Description</label>
            <Textarea
              value={formData.service_description}
              onChange={(e) => setFormData({ ...formData, service_description: e.target.value })}
              placeholder="Describe the service..."
              className="border-slate-200 resize-none"
              rows={2}
              data-testid="estimate-service-desc"
            />
          </div>
          
          {/* Line Items */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Line Items</label>
            <div className="space-y-2">
              {formData.line_items.map((item, index) => (
                <LineItemRow
                  key={item.id || `line-item-${index}`}
                  item={item}
                  index={index}
                  onChange={handleLineItemChange}
                  onRemove={removeLineItem}
                  canRemove={formData.line_items.length > 1}
                />
              ))}
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addLineItem}
              className="mt-2"
              data-testid="add-line-item-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Item
            </Button>
          </div>
          
          {/* Totals */}
          <div className="bg-slate-50 rounded-lg p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-slate-600">Subtotal</span>
              <span className="font-medium">{formatCurrency(subtotal)}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <span className="text-slate-600">Tax Rate</span>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={formData.tax_rate}
                  onChange={(e) => setFormData({ ...formData, tax_rate: parseFloat(e.target.value) || 0 })}
                  className="w-20 h-8 border-slate-200 text-center"
                  data-testid="estimate-tax-rate"
                />
                <span className="text-slate-500">%</span>
              </div>
              <span className="font-medium">{formatCurrency(taxAmount)}</span>
            </div>
            <div className="flex justify-between text-lg font-bold pt-2 border-t border-slate-200">
              <span className="text-[#1e3a5f]">Total</span>
              <span className="text-[#1e3a5f]">{formatCurrency(total)}</span>
            </div>
          </div>
          
          {/* Notes & Valid Until */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Valid Until</label>
              <Input
                type="date"
                value={formData.valid_until}
                onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                className="border-slate-200"
                data-testid="estimate-valid-until"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#d4a853]/50"
                data-testid="estimate-status-select"
              >
                <option value="draft">Draft</option>
                <option value="sent">Sent</option>
                <option value="accepted">Accepted</option>
                <option value="declined">Declined</option>
                <option value="expired">Expired</option>
              </select>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Notes / Terms</label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Payment terms, conditions, etc."
              className="border-slate-200 resize-none"
              rows={2}
              data-testid="estimate-notes"
            />
          </div>
          
          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="flex-1 bg-[#1e3a5f] hover:bg-[#152a45]"
              data-testid="estimate-save-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {estimate ? "Update" : "Create"} Estimate
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Estimate Detail Modal
const EstimateDetailModal = ({ estimate, onClose, onStatusChange, onEdit, onDelete }) => {
  const [changingStatus, setChangingStatus] = useState(false);

  const handleStatusChange = async (newStatus) => {
    setChangingStatus(true);
    try {
      await onStatusChange(estimate.id, newStatus);
    } finally {
      setChangingStatus(false);
    }
  };

  const copyToClipboard = () => {
    const text = `
ESTIMATE ${estimate.estimate_number}
From: Best Quality Leads Professional
To: ${estimate.customer_name}
${estimate.customer_email ? `Email: ${estimate.customer_email}` : ''}

${estimate.service_description ? `Service: ${estimate.service_description}\n` : ''}
ITEMS:
${estimate.line_items.map(item => 
  `- ${item.description}: ${item.quantity} x ${formatCurrency(item.unit_price)} = ${formatCurrency(item.total)}`
).join('\n')}

Subtotal: ${formatCurrency(estimate.subtotal)}
Tax (${estimate.tax_rate}%): ${formatCurrency(estimate.tax_amount)}
TOTAL: ${formatCurrency(estimate.total)}

${estimate.valid_until ? `Valid until: ${formatDate(estimate.valid_until)}` : ''}
${estimate.notes ? `\nNotes: ${estimate.notes}` : ''}
    `.trim();
    
    navigator.clipboard.writeText(text);
    toast.success("Estimate copied to clipboard");
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="estimate-detail-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-4 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-lg font-bold text-[#1e3a5f]">{estimate.estimate_number}</h2>
            <p className="text-sm text-slate-500">Created {formatDate(estimate.created_at)}</p>
          </div>
          <div className="flex items-center gap-2">
            <StatusBadge status={estimate.status} />
            <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-4 space-y-6">
          {/* Customer Info */}
          <div className="bg-slate-50 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-slate-500 uppercase mb-2">Customer</h3>
            <p className="font-medium text-[#1e3a5f] text-lg">{estimate.customer_name}</p>
            {estimate.customer_email && (
              <p className="text-slate-600">{estimate.customer_email}</p>
            )}
          </div>
          
          {/* Service Description */}
          {estimate.service_description && (
            <div>
              <h3 className="text-sm font-semibold text-slate-500 uppercase mb-2">Service Description</h3>
              <p className="text-slate-700">{estimate.service_description}</p>
            </div>
          )}
          
          {/* Line Items */}
          <div>
            <h3 className="text-sm font-semibold text-slate-500 uppercase mb-2">Items</h3>
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left text-xs font-medium text-slate-500 uppercase px-4 py-2">Description</th>
                    <th className="text-center text-xs font-medium text-slate-500 uppercase px-4 py-2 w-20">Qty</th>
                    <th className="text-right text-xs font-medium text-slate-500 uppercase px-4 py-2 w-28">Price</th>
                    <th className="text-right text-xs font-medium text-slate-500 uppercase px-4 py-2 w-28">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {estimate.line_items.map((item, index) => (
                    <tr key={item.id || `${item.description}-${index}`} className="border-t border-slate-100">
                      <td className="px-4 py-3 text-slate-700">{item.description}</td>
                      <td className="px-4 py-3 text-center text-slate-600">{item.quantity}</td>
                      <td className="px-4 py-3 text-right text-slate-600">{formatCurrency(item.unit_price)}</td>
                      <td className="px-4 py-3 text-right font-medium text-[#1e3a5f]">{formatCurrency(item.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          
          {/* Totals */}
          <div className="bg-slate-50 rounded-lg p-4 space-y-2">
            <div className="flex justify-between">
              <span className="text-slate-600">Subtotal</span>
              <span className="font-medium">{formatCurrency(estimate.subtotal)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">Tax ({estimate.tax_rate}%)</span>
              <span className="font-medium">{formatCurrency(estimate.tax_amount)}</span>
            </div>
            <div className="flex justify-between text-xl font-bold pt-2 border-t border-slate-200">
              <span className="text-[#1e3a5f]">Total</span>
              <span className="text-[#1e3a5f]">{formatCurrency(estimate.total)}</span>
            </div>
          </div>
          
          {/* Valid Until & Notes */}
          {(estimate.valid_until || estimate.notes) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {estimate.valid_until && (
                <div>
                  <h3 className="text-sm font-semibold text-slate-500 uppercase mb-1">Valid Until</h3>
                  <p className="text-slate-700">{formatDate(estimate.valid_until)}</p>
                </div>
              )}
              {estimate.notes && (
                <div className="sm:col-span-2">
                  <h3 className="text-sm font-semibold text-slate-500 uppercase mb-1">Notes / Terms</h3>
                  <p className="text-slate-700 whitespace-pre-wrap">{estimate.notes}</p>
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Actions */}
        <div className="p-4 border-t border-slate-100 bg-slate-50 flex flex-wrap gap-2">
          {estimate.status === "draft" && (
            <>
              <Button
                onClick={() => onEdit(estimate)}
                variant="outline"
                className="flex-1 sm:flex-none"
              >
                <Edit2 className="w-4 h-4 mr-2" />
                Edit
              </Button>
              <Button
                onClick={() => handleStatusChange("sent")}
                disabled={changingStatus}
                className="flex-1 sm:flex-none bg-blue-600 hover:bg-blue-700"
              >
                <Send className="w-4 h-4 mr-2" />
                Mark as Sent
              </Button>
            </>
          )}
          {estimate.status === "sent" && (
            <>
              <Button
                onClick={() => handleStatusChange("accepted")}
                disabled={changingStatus}
                className="flex-1 sm:flex-none bg-emerald-600 hover:bg-emerald-700"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Mark Accepted
              </Button>
              <Button
                onClick={() => handleStatusChange("declined")}
                disabled={changingStatus}
                variant="outline"
                className="flex-1 sm:flex-none text-red-600 border-red-200 hover:bg-red-50"
              >
                <XCircle className="w-4 h-4 mr-2" />
                Mark Declined
              </Button>
            </>
          )}
          <Button
            onClick={copyToClipboard}
            variant="outline"
            className="flex-1 sm:flex-none"
          >
            <Copy className="w-4 h-4 mr-2" />
            Copy
          </Button>
          <Button
            onClick={() => onDelete(estimate)}
            variant="outline"
            className="flex-1 sm:flex-none text-red-600 border-red-200 hover:bg-red-50"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete
          </Button>
        </div>
      </div>
    </div>
  );
};

// Empty State
const EmptyState = ({ onAddEstimate }) => (
  <div className="text-center py-16">
    <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
      <FileText className="w-8 h-8 text-slate-400" />
    </div>
    <h3 className="text-lg font-semibold text-[#1e3a5f] mb-2">No estimates yet</h3>
    <p className="text-slate-500 mb-4 max-w-sm mx-auto">
      Create your first estimate to send professional quotes to customers.
    </p>
    <Button onClick={onAddEstimate} className="bg-[#d4a853] hover:bg-[#b8922d] text-[#1e3a5f]">
      <Plus className="w-4 h-4 mr-2" />
      Create First Estimate
    </Button>
  </div>
);

// Main Component
export default function ProEstimatesPage() {
  const { token } = useAuth();
  const [estimates, setEstimates] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showFormModal, setShowFormModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [editingEstimate, setEditingEstimate] = useState(null);
  const [viewingEstimate, setViewingEstimate] = useState(null);

  // Fetch estimates
  const fetchEstimates = useCallback(async () => {
    if (!token) return;
    
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.append("status", statusFilter);
      if (searchQuery) params.append("search", searchQuery);
      
      const response = await axios.get(`${API}/pro/estimates?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEstimates(response.data.estimates || []);
    } catch (error) {
      toast.error("Failed to load estimates");
    } finally {
      setLoading(false);
    }
  }, [token, statusFilter, searchQuery]);

  // Fetch clients for dropdown
  const fetchClients = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await axios.get(`${API}/pro/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(response.data.clients || []);
    } catch (error) {
      // Silently fail
    }
  }, [token]);

  useEffect(() => {
    fetchEstimates();
    fetchClients();
  }, [fetchEstimates, fetchClients]);

  // Create estimate
  const handleCreate = async (data) => {
    try {
      await axios.post(`${API}/pro/estimates`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Estimate created");
      fetchEstimates();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create estimate");
      throw error;
    }
  };

  // Update estimate
  const handleUpdate = async (data) => {
    if (!editingEstimate) return;
    
    try {
      await axios.put(`${API}/pro/estimates/${editingEstimate.id}`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Estimate updated");
      fetchEstimates();
      
      // Update viewing estimate if it's the same one
      if (viewingEstimate?.id === editingEstimate.id) {
        const response = await axios.get(`${API}/pro/estimates/${editingEstimate.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setViewingEstimate(response.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update estimate");
      throw error;
    }
  };

  // Update status
  const handleStatusChange = async (estimateId, newStatus) => {
    try {
      await axios.put(`${API}/pro/estimates/${estimateId}/status`, 
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Estimate marked as ${newStatus}`);
      fetchEstimates();
      
      // Update viewing estimate
      if (viewingEstimate?.id === estimateId) {
        setViewingEstimate({ ...viewingEstimate, status: newStatus });
      }
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  // Delete estimate
  const handleDelete = async (estimate) => {
    if (!window.confirm(`Delete estimate ${estimate.estimate_number}?`)) return;
    
    try {
      await axios.delete(`${API}/pro/estimates/${estimate.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Estimate deleted");
      setEstimates(prev => prev.filter(e => e.id !== estimate.id));
      setShowDetailModal(false);
      setViewingEstimate(null);
    } catch (error) {
      toast.error("Failed to delete estimate");
    }
  };

  // Handlers
  const handleView = (estimate) => {
    setViewingEstimate(estimate);
    setShowDetailModal(true);
  };

  const handleEdit = (estimate) => {
    setEditingEstimate(estimate);
    setShowFormModal(true);
    setShowDetailModal(false);
  };

  const handleAddNew = () => {
    setEditingEstimate(null);
    setShowFormModal(true);
  };

  const handleCloseFormModal = () => {
    setShowFormModal(false);
    setEditingEstimate(null);
  };

  const handleCloseDetailModal = () => {
    setShowDetailModal(false);
    setViewingEstimate(null);
  };

  // Stats
  const stats = {
    total: estimates.length,
    draft: estimates.filter(e => e.status === "draft").length,
    sent: estimates.filter(e => e.status === "sent").length,
    accepted: estimates.filter(e => e.status === "accepted").length,
    totalValue: estimates.reduce((sum, e) => sum + (e.total || 0), 0),
    acceptedValue: estimates.filter(e => e.status === "accepted").reduce((sum, e) => sum + (e.total || 0), 0)
  };

  return (
    <div className="space-y-6" data-testid="pro-estimates-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1e3a5f]">Estimates</h1>
          <p className="text-slate-500">Create and manage price quotes for customers</p>
        </div>
        
        <Button
          onClick={handleAddNew}
          className="bg-[#d4a853] hover:bg-[#b8922d] text-[#1e3a5f]"
          data-testid="create-estimate-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Estimate
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="border-slate-200">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-[#1e3a5f]">{stats.total}</p>
            <p className="text-xs text-slate-500">Total Estimates</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-blue-600">{stats.sent}</p>
            <p className="text-xs text-slate-500">Sent / Pending</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-emerald-600">{stats.accepted}</p>
            <p className="text-xs text-slate-500">Accepted</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200 bg-emerald-50">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-emerald-600">{formatCurrency(stats.acceptedValue)}</p>
            <p className="text-xs text-slate-500">Accepted Value</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by customer or estimate #..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 border-slate-200"
                data-testid="estimates-search"
              />
            </div>
            
            {/* Status Filter */}
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#d4a853]/50"
                data-testid="estimates-status-filter"
              >
                <option value="all">All Status</option>
                <option value="draft">Draft</option>
                <option value="sent">Sent</option>
                <option value="accepted">Accepted</option>
                <option value="declined">Declined</option>
                <option value="expired">Expired</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Estimates List */}
      {loading ? (
        <div className="text-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-[#d4a853] mx-auto" />
        </div>
      ) : estimates.length === 0 && !searchQuery && statusFilter === "all" ? (
        <Card className="border-slate-200">
          <CardContent className="p-0">
            <EmptyState onAddEstimate={handleAddNew} />
          </CardContent>
        </Card>
      ) : estimates.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="p-8 text-center">
            <p className="text-slate-500">No estimates match your filters</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {estimates.map((estimate) => (
            <EstimateCard
              key={estimate.id}
              estimate={estimate}
              onView={handleView}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      {/* Form Modal */}
      {showFormModal && (
        <EstimateFormModal
          estimate={editingEstimate}
          clients={clients}
          onSave={editingEstimate ? handleUpdate : handleCreate}
          onClose={handleCloseFormModal}
        />
      )}

      {/* Detail Modal */}
      {showDetailModal && viewingEstimate && (
        <EstimateDetailModal
          estimate={viewingEstimate}
          onClose={handleCloseDetailModal}
          onStatusChange={handleStatusChange}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
}
